import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '../lib/cn.js';
export function InlineEdit({ value, onSubmit, multiline = false, placeholder, ariaLabel, savingLabel, savingFailedLabel, disabled = false, className, }) {
    const [phase, setPhase] = useState('read');
    const [draft, setDraft] = useState(value);
    const inputRef = useRef(null);
    const skipBlurRef = useRef(false);
    const fieldId = useId();
    // Keep draft in sync when an external `value` change lands (e.g. an
    // optimistic store roll-back) — but only while we're in read state,
    // so we never overwrite a half-typed edit.
    useEffect(() => {
        if (phase === 'read') {
            setDraft(value);
        }
    }, [value, phase]);
    // Focus + select on enter-edit. Calling `.select()` on a textarea
    // selects all text so the user can over-type without clearing.
    useEffect(() => {
        if (phase !== 'edit')
            return;
        const el = inputRef.current;
        if (!el)
            return;
        el.focus();
        el.select();
    }, [phase]);
    const commit = useCallback(async (next) => {
        const trimmed = next;
        if (trimmed === value) {
            setPhase('read');
            return;
        }
        setPhase('saving');
        try {
            await onSubmit(trimmed);
            setPhase('read');
        }
        catch (err) {
            // Re-throw with the localized failure label so the consumer's
            // catch/toast surfaces the right copy. Preserve the original
            // cause for debuggers.
            setPhase('edit');
            setDraft(trimmed);
            const message = err instanceof Error && err.message ? err.message : savingFailedLabel;
            throw new Error(message, { cause: err });
        }
    }, [onSubmit, savingFailedLabel, value]);
    const revert = useCallback(() => {
        setDraft(value);
        setPhase('read');
    }, [value]);
    function onChange(e) {
        setDraft(e.target.value);
    }
    function onKey(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            // Suppress the imminent blur — onBlur fires after Escape unfocuses.
            skipBlurRef.current = true;
            revert();
            return;
        }
        if (e.key === 'Enter') {
            const shouldCommit = multiline ? e.metaKey || e.ctrlKey : !e.shiftKey;
            if (shouldCommit) {
                e.preventDefault();
                skipBlurRef.current = true;
                void commit(draft).catch(() => {
                    // Swallow here — the throw inside commit is for the consumer's
                    // catch via the promise returned from onSubmit. The local
                    // catch is just so React's onKey doesn't propagate an unhandled
                    // rejection through the synthetic event.
                });
            }
        }
    }
    function onBlur() {
        if (skipBlurRef.current) {
            skipBlurRef.current = false;
            return;
        }
        if (phase !== 'edit')
            return;
        void commit(draft).catch(() => {
            // ditto — keep the rejection inside the inline-edit's commit
            // path; the consumer's onSubmit promise is the single surface.
        });
    }
    const displayValue = phase === 'saving' ? draft : value;
    const isEmpty = displayValue.length === 0;
    if (phase === 'edit') {
        const sharedClass = cn('w-full rounded-2 border border-[var(--color-line-2)] bg-[var(--color-surface)]', 'px-3 py-1.5 text-sm text-[var(--color-ink)] outline-none', 'focus-visible:border-[var(--color-accent)] focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]');
        return (_jsx("div", { className: cn('w-full', className), children: multiline ? (_jsx("textarea", { id: fieldId, ref: (el) => {
                    inputRef.current = el;
                }, value: draft, onChange: onChange, onKeyDown: onKey, onBlur: onBlur, placeholder: placeholder, "aria-label": ariaLabel, disabled: disabled, className: cn(sharedClass, 'min-h-[80px] resize-y leading-relaxed') })) : (_jsx("input", { id: fieldId, ref: (el) => {
                    inputRef.current = el;
                }, type: "text", value: draft, onChange: onChange, onKeyDown: onKey, onBlur: onBlur, placeholder: placeholder, "aria-label": ariaLabel, disabled: disabled, className: sharedClass })) }));
    }
    return (_jsxs("button", { type: "button", "aria-label": ariaLabel, disabled: disabled || phase === 'saving', onClick: () => {
            if (disabled)
                return;
            setDraft(value);
            setPhase('edit');
        }, className: cn('group inline-flex w-full items-center gap-2 rounded-2 px-2 py-1 -mx-2 -my-1 text-start text-sm transition-colors', 'hover:bg-[color-mix(in_oklab,var(--color-accent)_6%,transparent)]', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]', 'disabled:cursor-not-allowed disabled:opacity-60', className), children: [_jsx("span", { className: cn('min-w-0 flex-1 truncate', isEmpty ? 'text-[var(--color-ink-faint)] italic' : 'text-[var(--color-ink)]'), children: isEmpty ? placeholder : displayValue }), phase === 'saving' && (_jsx("span", { className: "font-mono text-2xs uppercase tracking-[0.12em] text-[var(--color-ink-muted)]", children: savingLabel })), phase === 'read' && (_jsx(Pencil, { className: cn('size-3 shrink-0 text-[var(--color-ink-muted)] opacity-0 transition-opacity', 'group-hover:opacity-100 group-hover:text-[var(--color-accent)]', 'group-focus-visible:opacity-100'), "aria-hidden": true }))] }));
}
